<template>
  <Card
    ref="cardRef"
    style="height: calc(100vh - 180px)"
    class="!my-0"
    full-height
  >
    <AgreementConversations
      ref="conversationsRef"
      style="height: 100%"
      :agreement="agreementData"
      :conversations="conversationsData ?? undefined"
      :is-visible="isVisible"
      @new-conversation-submitted="$emit('new-conversation-submitted')"
    />
  </Card>
</template>

<script setup lang="ts">
// vue
import { ref, onMounted, nextTick } from 'vue'
// components
import Card from '@/components/card/Card.vue'
import AgreementConversations from '@/components/unique/agreement/AgreementConversations.vue'
// types
import type { Agreement } from '@/types/domain/agreement'

interface Props {
  agreementData: Agreement
  conversationsData: Array<Record<string, unknown>> | null
}

defineProps<Props>()

defineEmits<{
  'new-conversation-submitted': []
}>()

/**
 * Tracks visibility of this tab component
 * Initially false, will be set to true when component becomes visible
 */
const isVisible = ref(false)
const cardRef = ref<InstanceType<typeof Card> | null>(null)
const conversationsRef = ref<InstanceType<typeof AgreementConversations> | null>(null)

/**
 * Scrolls conversations to bottom
 * Exposed for parent component to call
 */
function scrollToBottom() {
  nextTick(() => {
    if (conversationsRef.value && typeof conversationsRef.value.scrollToBottom === 'function') {
      conversationsRef.value.scrollToBottom()
    }
  })
}

// Watch for when component becomes visible
onMounted(() => {
  // Use a simple interval to check visibility initially
  // This handles the case when v-show makes the component visible
  const checkVisibility = () => {
    if (cardRef.value) {
      const element = (cardRef.value as InstanceType<typeof Card>).$el as HTMLElement
      if (element) {
        const style = window.getComputedStyle(element)
        const wasVisible = isVisible.value
        isVisible.value = style.display !== 'none' && element.offsetHeight > 0

        // If just became visible, scroll to bottom
        if (!wasVisible && isVisible.value) {
          scrollToBottom()
        }
      }
    }
  }

  // Check immediately and then periodically for a short time
  checkVisibility()
  const interval = setInterval(() => {
    checkVisibility()
  }, 100)

  // Stop checking after 2 seconds
  setTimeout(() => {
    clearInterval(interval)
    // Final check
    checkVisibility()
  }, 2000)
})

// Expose method for parent to call
defineExpose({
  scrollToBottom
})
</script>
